import React, { FC, PropsWithChildren, useContext, useRef, useState } from 'react';

import { isEqual } from 'lodash-es';

import { ISanityImage } from '@rbi-ctg/menu';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isCombo, mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { MIN_CART_QUANTITY } from 'utils/cart';
import noop from 'utils/noop';
import { useMemoAll } from 'utils/use-memo-all';

import {
  FlowDirection,
  FlowStep,
  IProductWizardContext,
  IProductWizardProvider,
  PickerSelections,
  SlotsSelections,
} from './types';
import {
  buildFlow,
  computeSelectedProduct,
  initializeSlotModifiers,
  initializeSlotSelections,
  initializeSlotSelectionsFromCartEntry,
} from './utils';

/**
 * Provides access to menu data and manage the state of the ProductDetail page.
 */
const ProductWizardContext = React.createContext<IProductWizardContext>({
  menuObject: ({} as unknown) as IProductWizardContext['menuObject'],
  selectedProduct: null as any,
  selectedProductImage: (null as unknown) as ISanityImage,
  setSelectedProduct: () => undefined,
  productQuantity: MIN_CART_QUANTITY,
  setProductQuantity: () => undefined,
  showCloseDialog: false,
  setShowCloseDialog: () => undefined,
  pickerSelections: {},
  selectPickerAspectOption: noop,
  slotsSelections: {},
  selectComboSlotItem: noop,
  selectModifierMultiplier: noop,
  flow: [],
  saveSlotsSelectionSnapshot: noop,
  restoreSlotsSelectionSnapshot: noop,
  clearSlotsSelectionSnapshot: noop,
  hasUnsavedChanges: () => false,
  isAnySlotCustomized: () => false,
  flowDirection: 'forward',
  setFlowDirection: () => undefined,
  removeSlotSelection: noop,
});

export const useProductWizardContext = () => useContext(ProductWizardContext);

export const ProductWizardProvider: FC<PropsWithChildren<IProductWizardProvider>> = ({
  menuObject,
  editingCartEntry,
  children,
}) => {
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();

  const [flowDirection, setFlowDirection] = useState<FlowDirection>('forward');

  const [selectedProduct, setSelectedProduct] = useState<ILocalizedMenuEntry>(() =>
    computeSelectedProduct(indexedMenu, menuObject, posServiceMode, undefined, editingCartEntry)
  );
  const [productQuantity, setProductQuantity] = useState<number>(MIN_CART_QUANTITY);

  const [pickerSelections, setPickerSelections] = useState<PickerSelections>({});

  const [slotsSelections, setSlotsSelections] = useState<SlotsSelections>(() =>
    editingCartEntry
      ? initializeSlotSelectionsFromCartEntry(selectedProduct, indexedMenu, editingCartEntry)
      : initializeSlotSelections(selectedProduct, indexedMenu, isCombo(menuObject), posServiceMode)
  );

  // Stores a snapshot of the slots selections when the user goes to "edit"
  // this will serve as the undo state when the user cancels the edit
  const slotsSelectionsSnapshot = useRef<SlotsSelections | undefined>(undefined);

  const [showCloseDialog, setShowCloseDialog] = useState<boolean>(false);

  const [flow, setFlow] = useState<FlowStep[]>(
    buildFlow(menuObject, indexedMenu, pickerSelections, posServiceMode)
  );

  const selectPickerAspectOption: IProductWizardContext['selectPickerAspectOption'] = (
    pickerAspectKey,
    pickerAspectOptionId
  ) => {
    setPickerSelections(prevPickerSelections => {
      const updatedSelections = {
        ...prevPickerSelections,
        [pickerAspectKey]: pickerAspectOptionId,
      };

      setFlow(buildFlow(menuObject, indexedMenu, updatedSelections, posServiceMode));

      setSelectedProduct(prevSelectedProduct => {
        const updatedProduct = computeSelectedProduct(
          indexedMenu,
          menuObject,
          posServiceMode,
          updatedSelections,
          undefined,
          pickerAspectKey
        );
        if (updatedProduct.id !== prevSelectedProduct.id) {
          setSlotsSelections(
            initializeSlotSelections(
              updatedProduct,
              indexedMenu,
              isCombo(menuObject),
              posServiceMode
            )
          );
          return updatedProduct;
        }
        return prevSelectedProduct;
      });

      return updatedSelections;
    });
  };

  const selectComboSlotItem: IProductWizardContext['selectComboSlotItem'] = (slotKey, itemId) => {
    setSlotsSelections(prevSlotsSelections => {
      const previousItemId = prevSlotsSelections[slotKey]?.itemId;
      const newItemWasSelected = itemId !== previousItemId;
      return {
        ...prevSlotsSelections,
        [slotKey]: {
          ...prevSlotsSelections[slotKey],
          itemId,
          ...(newItemWasSelected && {
            modifiers: initializeSlotModifiers(
              indexedMenu,
              itemId,
              posServiceMode,
              true,
              isCombo(menuObject)
            ),
          }),
        },
      };
    });
  };

  const selectModifierMultiplier: IProductWizardContext['selectModifierMultiplier'] = (
    slotKey,
    itemId,
    modifierKey,
    multipliersSelection
  ) => {
    setSlotsSelections(prevSlotsSelections => ({
      ...prevSlotsSelections,
      [slotKey]: {
        itemId,
        modifiers: {
          ...(prevSlotsSelections[slotKey]?.modifiers ?? {}),
          [modifierKey]: multipliersSelection,
        },
      },
    }));
  };

  const saveSlotsSelectionSnapshot: IProductWizardContext['saveSlotsSelectionSnapshot'] = () => {
    // We do not need to make a deep copy of the object since all operations are immutable
    slotsSelectionsSnapshot.current = slotsSelections;
  };

  const restoreSlotsSelectionSnapshot: IProductWizardContext['restoreSlotsSelectionSnapshot'] = () => {
    if (slotsSelectionsSnapshot.current) {
      setSlotsSelections(slotsSelectionsSnapshot.current);
      slotsSelectionsSnapshot.current = undefined;
    }
  };

  const clearSlotsSelectionSnapshot: IProductWizardContext['clearSlotsSelectionSnapshot'] = () => {
    slotsSelectionsSnapshot.current = undefined;
  };

  const removeSlotSelection: IProductWizardContext['removeSlotSelection'] = slotKey => {
    setSlotsSelections(prev => {
      const { [slotKey]: removed, ...remaining } = prev;
      return remaining;
    });
  };

  const hasUnsavedChanges: IProductWizardContext['hasUnsavedChanges'] = () => {
    const snapshot = slotsSelectionsSnapshot.current;
    if (!snapshot || snapshot === slotsSelections) {
      return false;
    }

    const equal = isEqual(snapshot, slotsSelections);
    return !equal;
  };

  const selectedProductImage: ISanityImage = mapImageResourceToSanityImage(
    selectedProduct.image?.resource ?? '',
    selectedProduct.image?.altText?.locale ?? ''
  );

  const isAnySlotCustomized = (): boolean => {
    const defaultSlotSelections = initializeSlotSelections(
      selectedProduct,
      indexedMenu,
      isCombo(menuObject),
      posServiceMode
    );
    return !isEqual(slotsSelections, defaultSlotSelections);
  };

  const value = useMemoAll<IProductWizardContext>({
    menuObject,
    selectedProduct,
    selectedProductImage,
    setSelectedProduct,
    productQuantity,
    setProductQuantity,
    showCloseDialog,
    setShowCloseDialog,
    pickerSelections,
    selectPickerAspectOption,
    slotsSelections,
    selectComboSlotItem,
    selectModifierMultiplier,
    flow,
    saveSlotsSelectionSnapshot,
    restoreSlotsSelectionSnapshot,
    clearSlotsSelectionSnapshot,
    hasUnsavedChanges,
    isAnySlotCustomized,
    flowDirection,
    setFlowDirection,
    removeSlotSelection,
  });

  return <ProductWizardContext.Provider value={value}>{children}</ProductWizardContext.Provider>;
};
